import { useEffect, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiShipmentRequestTimeoutMs } from "../config";
import useApiHelper from "../hooks/useApiHelper";
import { IPart } from "../models/Part";
import { ICustomShippingRequest, IProoviaCollectionSlotRequest, IProoviaItemsResponse, IProoviaSlot, IShippingCreateRequest, IShippingInfoResponse, IShippingQuote, IShippingQuotesRequest } from "../models/Shipping";
import { IProoviaDeliverySlotRequest } from "./../models/Shipping";
import { DefaultResponseWithData } from "./../models/system";
import useShippingProviders from "./useShippingProviders";

export default function useShipping(part: IPart) {
  const { shippingProviders: providers } = useShippingProviders();
  const [isValidating, setIsValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const staleTime = 1000 * 60 * 10; // 10 minutes

  useEffect(() => {
    // TODO: Make this all dynamic based on available providers once the API supports voila
    if (!providers.data || providers.isLoading) return;

    const availableProviders = providers.data.data.map(provider => {
      const settings = provider.requiredSettings ?? {};

      const allSet = Object.keys(settings).length > 0 && Object.values(settings).every((val) => val?.trim() !== "");

      if (!allSet) return null;

      return {
        value: 0,
        label: provider.providerName,
      };
    }).filter(provider => provider !== null);

    let prooviaProvider = availableProviders.find(p => p?.label === "Proovia");
    if (prooviaProvider) setValid(true);
    else setValid(false);

    setIsValidating(false);
  }, [providers.data, providers.isLoading]);

  const { get, getFile, post, del } = useApiHelper();

  // Booking a shipment is the one call here that waits on a third party mid-request - the API
  // holds it open while the courier publishes the label. Everything else on this hook keeps the
  // 60s default; only the create gets the longer leash.
  const { postRaw } = useApiHelper({ timeoutMs: apiShipmentRequestTimeoutMs });
  const queryClient = useQueryClient();

  const shippingInfo = useQuery(
    ["shipping", part.id],
    () => get<IShippingInfoResponse>(`/shipping/${part.id}`),
    {
      staleTime: staleTime,
    }
  );

  const prooviaItems = useQuery(
    ["prooviaShipping", part.id],
    () => get<IProoviaItemsResponse>("/shipping/proovia/items"),
    {
      enabled: valid && !isValidating,
      staleTime: staleTime,
    }
  );

  const prooviaCollectionDates = useMutation(
    (req: IProoviaCollectionSlotRequest) => {
      if (!valid || isValidating) return Promise.reject(new Error("Proovia shipping is not enabled."));
      return get<DefaultResponseWithData<IProoviaSlot[]>>(
        `/shipping/proovia/slots/collection?from=${req.from}&to=${req.to}`
      )
    }
  );

  const prooviaDeliveryDates = useMutation((req: IProoviaDeliverySlotRequest) => {
    if (!valid || isValidating) return Promise.reject(new Error("Proovia shipping is not enabled."));
    return get<DefaultResponseWithData<IProoviaSlot[]>>(
      `/shipping/proovia/slots/delivery?from=${req.from}&to=${req.to}&collectionDate=${req.collectionDate}`
    )
  }

  );

  const customShipping = useMutation(
    (req: ICustomShippingRequest) => post(`/shipping/custom`, req),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", part.id]);
      },
    }
  );

  const quotes = useMutation(
    (req: IShippingQuotesRequest) =>
      post<IShippingQuotesRequest, DefaultResponseWithData<IShippingQuote[]>>(
        `/shipping/quotes`,
        req
      ),
    {}
  );

  const shippingLabel = useMutation(
    (quote: IShippingCreateRequest) => postRaw(`/shipping`, quote),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", part.id]);
      },
    }
  );

  // Re-reading an existing label must not go through POST /shipping. That endpoint creates a
  // shipment, and the only thing stopping a second collection being booked is a guard on the
  // stored document - so viewing a label is a plain GET of the document we already have.
  const downloadShippingLabel = useMutation((documentId: string) =>
    getFile(`/part/${part.id}/documents/${documentId}`)
  );

  const cancelShipping = useMutation((_: any) => del(`/shipping/${part.id}`), {
    onSuccess: () => {
      queryClient.invalidateQueries(["parts", part.id]);
    },
  });

  return {
    shippingInfo,
    prooviaItems,
    prooviaCollectionDates,
    prooviaDeliveryDates,
    quotes,
    shippingLabel,
    downloadShippingLabel,
    cancelShipping,
    customShipping,
  };
}
